// src/pages/ProductsPage.jsx
import React, { useState, useEffect } from 'react';
import productService from '../services/productService';
import ProductFilter from '../components/product/ProductFilter';
import ProductGrid from '../components/product/ProductGrid';
import { scrollToTop, seededShuffle } from '../utils/helpers';

/**
 * Products Page Component
 * 
 * Features:
 * - Filter by college (main feature)
 * - Filter by category
 * - Search products
 * - Display products in grid
 */
const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    collegeId: null,
    category: null,
    search: '',
  });
  
  // Fetch products on mount and when filters change
  useEffect(() => {
    fetchProducts();
    scrollToTop();
  }, [filters]);
  
  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      let data;
      
      // Filter by college and category
      if (filters.collegeId && filters.category) {
        data = await productService.getProductsByCollegeAndCategory(
          filters.collegeId,
          filters.category
        );
      }
      // Filter by college only
      else if (filters.collegeId) {
        data = await productService.getProductsByCollege(filters.collegeId);
      }
      // Filter by category only
      else if (filters.category) {
        data = await productService.getProductsByCategory(filters.category);
      }
      // No filters - get all products
      else {
        data = await productService.getAllProducts();
      }
      
      // Apply search filter locally
      if (filters.search) {
        data = data.filter(product =>
          product.name.toLowerCase().includes(filters.search.toLowerCase()) ||
          product.description?.toLowerCase().includes(filters.search.toLowerCase())
        );
      }
      
      // Randomize order so no single college always sits at the top.
      // Seed is stable per day (IST), so the order rotates daily but
      // stays consistent within a visit (no reshuffle on re-render).
      setProducts(seededShuffle(data));
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Shop College Merchandise
          </h1>
          <p className="text-gray-600">
            Browse our collection of premium T-shirts and hoodies
          </p>
        </div>
        
        {/* Product Filter */}
        <ProductFilter
          onFilterChange={handleFilterChange}
          currentFilters={filters}
        />
        
        {/* Products Grid */}
        <ProductGrid products={products} loading={loading} />
      </div>
    </div>
  );
};

export default ProductsPage;